// Export-template registry (statutory-submissions program): destination upload
// formats (NSSF/WCF/CRDB bank …) as DB-governed, versioned, effective-dated
// rows. A PUBLISHED row overrides the disk country-pack YAML at generation
// time — a format change or a new customer's bank is configuration, not a
// deploy. Draft/publish is maker-checker (author ≠ publisher).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/payroll/platform/export-templates`;

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
const H = () => ({ headers: { ...authHeader() } });

export type TemplateStatus = 'draft' | 'published' | 'retired';

export interface ExportTemplateRow {
  id: string;
  country_code: string;
  export_type: string;
  bank_code?: string;
  tenant_id?: string | null;
  template_version: string;
  spec: unknown; // same schema as country-packs/<cc>/exports/*.yaml, as JSON
  status: TemplateStatus;
  effective_from: string;
  effective_to?: string | null;
  created_by?: string | null;
  published_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiskDefault {
  country_code: string;
  export_type: string;
  bank_code?: string;
  template_version?: string;
  name?: string;
  source: 'disk';
}

export async function listExportTemplates(country?: string): Promise<{
  templates: ExportTemplateRow[];
  disk_defaults: DiskDefault[];
}> {
  const { data } = await axios.get(BASE, { ...H(), params: country ? { country } : undefined });
  return { templates: data.templates ?? [], disk_defaults: data.disk_defaults ?? [] };
}

export interface DraftBody {
  country_code: string;
  export_type: string;
  bank_code?: string;
  tenant_id?: string | null;
  template_version: string;
  effective_from?: string;
  spec: unknown;
}

export async function createExportTemplateDraft(body: DraftBody): Promise<ExportTemplateRow> {
  const { data } = await axios.post(BASE, body, H());
  return data;
}

export async function updateExportTemplateDraft(
  id: string,
  body: Pick<DraftBody, 'template_version' | 'effective_from' | 'spec'>,
): Promise<ExportTemplateRow> {
  const { data } = await axios.put(`${BASE}/${id}`, body, H());
  return data;
}

export async function publishExportTemplate(id: string): Promise<ExportTemplateRow> {
  const { data } = await axios.post(`${BASE}/${id}/publish`, {}, H());
  return data;
}

export async function retireExportTemplate(id: string): Promise<ExportTemplateRow> {
  const { data } = await axios.post(`${BASE}/${id}/retire`, {}, H());
  return data;
}
